import React, { useState, useEffect } from 'react';
import { Search, User, Clock, BookOpen } from 'lucide-react';
import axios from 'axios';

const TeacherWiseRoutine = () => {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [routine, setRoutine] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/staff/?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const staffData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      // Filter for both "Teacher" and "Teaching" roles
      setTeachers(staffData.filter(s => s.role === 'Teacher' || s.role === 'Teaching'));
    } catch (error) {
      console.error('Error fetching teachers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoutine = async (teacherId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/routine/teacher/${teacherId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoutine(response.data);
    } catch (error) {
      console.error('Error fetching routine:', error);
      setRoutine({ routine_by_day: {}, total_classes: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    fetchRoutine(teacher.id);
  };

  const filteredTeachers = teachers.filter(teacher =>
    teacher.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Teacher Wise Routine</h1>
        <p className="text-sm text-gray-500 mt-1">View class schedule for individual teachers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4">Select Teacher</h3>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search teachers..."
              className="pl-10 pr-4 py-2 w-full border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {loading ? (
              <p className="text-center text-gray-500 py-4">Loading...</p>
            ) : filteredTeachers.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No teachers found</p>
            ) : (
              filteredTeachers.map((teacher) => (
                <div
                  key={teacher.id}
                  onClick={() => handleTeacherSelect(teacher)}
                  className={`p-3 rounded cursor-pointer hover:bg-blue-50 ${
                    selectedTeacher?.id === teacher.id ? 'bg-blue-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{teacher.full_name}</p>
                      <p className="text-xs text-gray-500">{teacher.email}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {selectedTeacher ? (
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="h-8 w-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{selectedTeacher.full_name}</h3>
                    <p className="text-sm text-gray-500">{selectedTeacher.email}</p>
                    <p className="text-sm text-gray-500">Department: {selectedTeacher.department || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Weekly Schedule</h3>
                  {routine?.total_classes > 0 && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {routine.total_classes} {routine.total_classes === 1 ? 'class' : 'classes'}
                    </span>
                  )}
                </div>
                
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2 text-sm">Loading schedule...</p>
                  </div>
                ) : routine?.total_classes === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No classes scheduled for this teacher</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {daysOfWeek.map(day => {
                      const dayClasses = routine?.routine_by_day?.[day] || [];
                      return (
                        <div key={day} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-gray-600" />
                              <h4 className="font-semibold text-gray-900">{day}</h4>
                            </div>
                            {dayClasses.length > 0 && (
                              <span className="text-xs text-gray-500">
                                {dayClasses.length} {dayClasses.length === 1 ? 'class' : 'classes'}
                              </span>
                            )}
                          </div>
                          <div className="space-y-2">
                            {dayClasses.length === 0 ? (
                              <p className="text-sm text-gray-400 italic">No classes scheduled</p>
                            ) : (
                              dayClasses.map((classItem, idx) => (
                                <div key={idx} className="bg-blue-50 p-3 rounded">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <BookOpen className="h-4 w-4 text-blue-600" />
                                      <div>
                                        <span className="font-medium text-blue-900">
                                          {classItem.subject_name}
                                        </span>
                                        {classItem.subject_code && (
                                          <span className="text-xs text-gray-500 ml-2">
                                            ({classItem.subject_code})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <span className="text-sm text-gray-600 whitespace-nowrap">
                                      {classItem.start_time} - {classItem.end_time}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">
                                    {classItem.course_name} | {classItem.semester_name} | Section: {classItem.section_name}
                                  </p>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white p-12 rounded-lg shadow text-center">
              <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Select a teacher to view their routine</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherWiseRoutine;